import React, { useState } from 'react';
import { BillOfLading } from 'src/components/BillOfLading';
import LoadingIndicator from 'src/components/LoadingIndicator';
import {
  PayInvoiceModal,
  ShipmentInvoice
} from 'src/components/ShipmentInvoice';
import emptyImg from 'src/images/empty.svg';
import invoiceImage from 'src/images/invoice.svg';
import logo from 'src/images/logo-md.png';
import { usePreviewTrack } from 'src/lib/hooks';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Icon,
  Modal,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  TabPane,
  toast,
  useApp
} from 'straightline-ui';
import { formatMoney } from 'straightline-utils/accounting';
import { INVOICE_STATUS } from 'straightline-utils/constants';
import { formatDate } from 'straightline-utils/time';

export default function ShipmentInvoicePreview({ match, location }) {
  const app = useApp();
  const [activeTab, setActiveTab] = useState(
    new URLSearchParams(location.search).get('tab') || 'invoice'
  );

  const token = location.query.token;

  const {
    result: shipmentInvoice,
    isLoading,
    isError,
    error
  } = app.service('api/shipment-invoice-previews').useGet(
    token,
    {},
    {
      enabled: !!token
    }
  );

  usePreviewTrack(shipmentInvoice?.shipment, 'SHIPPER_INVOICE');

  if (isLoading) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (isError || !token) {
    return (
      <Container>
        <Row className="align-items-center justify-content-center vh-100">
          <Col lg="6" className="text-center">
            <img
              src={emptyImg}
              alt="Not Found"
              className="img-fluid animated fadeIn faster mb-5"
            />
            {error?.code === 404 ? (
              <h1>Could not find that invoice</h1>
            ) : (
              <h1>{error?.message || 'Could not find that invoice'}</h1>
            )}
            <ContactModal shipment={shipmentInvoice?.shipment} block />
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <div>
      <Container fluid className="fixed-top bg-white shadow-sm bg-white">
        <Row className="align-items-center py-2">
          <Col xs="4">
            <img src={logo} style={{ maxWidth: 138 }} alt="Straightline Logo" />
          </Col>
          <Col xs="8" className="ml-auto text-right">
            <a
              href={
                shipmentInvoice.shipper_id ===
                shipmentInvoice.shipment.shipper_id
                  ? `/shipments/${shipmentInvoice.shipment_id}?tab=invoice`
                  : '/invoices'
              }
              className="btn btn-white mr-3"
            >
              <Icon type="user" className="mr-2" />
              Login
            </a>
            <ContactModal shipment={shipmentInvoice.shipment} />
          </Col>
        </Row>
      </Container>
      <div style={{ marginTop: 54 }}>
        <Container fluid="lg" className="mb-5">
          <PageHeader
            shipment={shipmentInvoice.shipment}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </Container>
        <Container fluid="lg" className="pb-5">
          <TabContent activeTab={activeTab}>
            <TabPane tabId="bol">
              <BolTab shipment={shipmentInvoice.shipment} />
            </TabPane>
            <TabPane tabId="pod">
              <PodTab shipment={shipmentInvoice.shipment} />
            </TabPane>
            <TabPane tabId="invoice">
              <InvoiceTab token={token} shipmentInvoice={shipmentInvoice} />
            </TabPane>
          </TabContent>
        </Container>
      </div>
    </div>
  );
}

const InvoiceTab = ({ shipmentInvoice, token }) => {
  const app = useApp();

  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  const openPaymentInvoice = () => {
    setInvoiceModalOpen(true);
  };

  const closePaymentInvoice = () => {
    setInvoiceModalOpen(false);
  };

  const handlePayInvoice = async ({ payment_method_id }) => {
    await app.service('api/shipment-invoice-previews').update(token, {
      payment_method_id
    });
    app.service('api/shipment-invoice-previews').invalidateGet(token);
    toast.success('Payment successfully processed.');
    closePaymentInvoice();
  };

  return (
    <div>
      <PayInvoiceModal
        open={!!invoiceModalOpen}
        onCancel={closePaymentInvoice}
        onPay={handlePayInvoice}
        shipmentInvoice={shipmentInvoice}
        paymentMethods={shipmentInvoice.payment_methods}
      />
      <Row>
        <Col xl="4" className="order-xl-2">
          <Card>
            {shipmentInvoice.status === INVOICE_STATUS.OPEN && (
              <CardBody className="border-bottom">
                <Row className="d-flex justify-content-between mb-2">
                  <Col className="mb-1">
                    <div className="h3 mb-0 d-flex align-items-center">
                      <Icon type="dollar-sign" className="mr-3"></Icon>
                      Payment
                    </div>
                  </Col>
                  <Col className="col-auto mb-1">
                    {/* {new Date(shipmentInvoice.due_date) <= new Date() ? (
                    <div className="mb-0 text-danger">Overdue</div>
                  ) : (
                    <div className="mb-0 text-primary">
                      Due {formatDistanceToNowStrict(shipmentInvoice.due_date)}
                    </div>
                  )} */}
                  </Col>
                </Row>
                <div className="mb-3">
                  Select a payment method to complete your invoice. Additional
                  processing fees may apply. We'll show any fees before you
                  submit payment.
                </div>
                <div>
                  {shipmentInvoice.payment_methods?.length > 0 ? (
                    <Button
                      color="primary"
                      block
                      disabled={
                        shipmentInvoice.paid_amount >=
                        shipmentInvoice.due_amount
                      }
                      onClick={() => openPaymentInvoice()}
                    >
                      Pay Invoice
                    </Button>
                  ) : (
                    <Button color="primary" block to="/settings/billing">
                      Link Payment Method
                    </Button>
                  )}
                </div>
              </CardBody>
            )}
            <CardBody>
              <Row className="d-flex justify-content-between mb-2">
                <Col className="mb-1">
                  <div className="h3 mb-0 d-flex align-items-center">
                    <Icon type="file" className="mr-3"></Icon>Documents
                  </div>
                </Col>
                <Col className="col-auto mb-1">
                  {/* <div className="mb-0 text-secondary">
                  Updated{' '}
                  {formatDistanceToNowStrict(shipmentInvoice.created_at)}
                </div> */}
                </Col>
              </Row>
              <div className="mb-3">
                Download the latest invoice and POD for this shipment. Documents
                are updated automatically as new billing or delivery info
                becomes available.
              </div>
              <div>
                <Button
                  color="white"
                  block
                  tag="a"
                  href={`${shipmentInvoice.secure_url}?download=true`}
                >
                  Download Invoice
                </Button>
                {shipmentInvoice.shipment.pod_document && (
                  <Button
                    color="white"
                    block
                    tag="a"
                    href={`${shipmentInvoice.shipment.pod_document.document.download_url}`}
                  >
                    Download POD
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl="8">
          <div className="" key={shipmentInvoice.id}>
            <ShipmentInvoice shipmentInvoice={shipmentInvoice} />
            {shipmentInvoice.charges && (
              <Card className="p-4">
                <h2>Payments</h2>
                <div className="table-responsive">
                  <Table className="mb-0">
                    <TableHead>
                      <TableRow>
                        <TableHeader className="px-1 bg-transparent border-top-0">
                          <span className="h6">Payment</span>
                        </TableHeader>
                        <TableHeader className="px-1 bg-transparent border-top-0">
                          <span className="h6">Amount</span>
                        </TableHeader>
                        <TableHeader className="px-1 bg-transparent border-top-0">
                          <span className="h6">Fee</span>
                        </TableHeader>
                        <TableHeader className="px-1 bg-transparent border-top-0">
                          <span className="h6">Date</span>
                        </TableHeader>
                        <TableHeader className="px-1 bg-transparent border-top-0 text-right">
                          <span className="h6">Status</span>
                        </TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shipmentInvoice.charges.map((charge) => (
                        <TableRow key={charge.id}>
                          <TableData className="px-1">
                            <div>
                              {charge.payment_method.description ||
                                'Payment Method'}{' '}
                              -{' '}
                              {charge.payment_method.reference_number || '****'}
                            </div>
                            <div>
                              {charge.notes || (
                                <span className="text-muted">
                                  Not Available
                                </span>
                              )}
                            </div>
                          </TableData>
                          <TableData className="px-1">
                            {formatMoney(charge.amount)}
                          </TableData>
                          <TableData className="px-1">
                            {formatMoney(charge.fee_amount || 0)}
                          </TableData>
                          <TableData className="px-1">
                            {formatDate(charge.created_at)}
                          </TableData>
                          <TableData className="px-1 text-right">
                            {charge.refunded ? (
                              <React.Fragment>
                                <Badge color="soft-danger">REFUNDED</Badge>
                              </React.Fragment>
                            ) : (
                              <Badge color="soft-success">PAID</Badge>
                            )}
                          </TableData>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="border-top border-top-2 pt-4 d-flex justify-content-between">
                  <div>
                    <h3 className="mb-0">Total Paid</h3>
                  </div>
                  <div>
                    <h3 className="mb-0">
                      {formatMoney(
                        shipmentInvoice.charges.reduce((total, charge) => {
                          return charge.refunded
                            ? total
                            : total + charge.amount + charge.fee_amount;
                        }, 0)
                      )}
                    </h3>
                  </div>
                </div>
                <div className="pt-3">
                  <div className="d-flex justify-content-between mb-1">
                    <div>Payment Amount</div>
                    <div>
                      {formatMoney(
                        shipmentInvoice.charges.reduce((total, charge) => {
                          return charge.refunded
                            ? total
                            : total + charge.amount;
                        }, 0)
                      )}
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <div>Payment Fees</div>
                    <div>
                      {formatMoney(
                        shipmentInvoice.charges.reduce((total, charge) => {
                          return charge.refunded
                            ? total
                            : total + charge.fee_amount;
                        }, 0)
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

const PageHeader = ({ shipment, activeTab, setActiveTab }) => {
  return (
    <div className="header mb-0 border-bottom">
      <div className="header-body border-0">
        <Row className="font-weight-bold">
          <Col xs="auto" className="mb-3">
            <span className="text-muted">SL#: </span>
            {shipment.id}
          </Col>
          {shipment.reference_number && (
            <Col xs="auto" className="mb-3">
              <span className="text-muted">REF#: </span>
              {shipment.reference_number}
            </Col>
          )}
          {shipment.shipment_dispatch?.carrier_pro_number && (
            <Col xs="auto" className="mb-3">
              <span className="text-muted">PRO#: </span>
              {shipment.shipment_dispatch.carrier_pro_number}
            </Col>
          )}
          {shipment.mode && (
            <Col xs="auto" className="mb-3">
              <span className="text-muted">MODE: </span>
              {shipment.mode}
            </Col>
          )}
          {shipment.carrier && (
            <Col xs="auto" className="mb-3">
              <span className="text-muted">CARRIER: </span>
              {shipment.carrier.name}
            </Col>
          )}
          {shipment.rate && (
            <Col xs="auto" className="mb-3">
              <span className="text-muted">SERVICE: </span>
              {shipment.rate.service_level.name}
            </Col>
          )}
        </Row>
        <Row className="align-items-end justify-content-between mt-md-n3">
          <Col xs="auto">
            <Row className="align-items-center mb-n3">
              <Col xs="auto" className="mb-2 pr-0 align-items-center d-flex">
                <h1 className="mb-0 font-weight-bold d-inline-block mr-3">{`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}</h1>
                <Icon
                  type="arrow-right"
                  style={{ fontSize: 24 }}
                  className="text-success lead d-inline-block font-weight-bold"
                />
              </Col>
              <Col xs="auto" className="mb-2">
                <h1 className="mb-0 font-weight-bold d-inline-block">{`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}</h1>
              </Col>
            </Row>
          </Col>
          <Col xs="auto" className="text-right">
            <Nav className="nav nav-tabs header-tabs">
              <NavItem>
                <NavLink
                  active={activeTab === 'bol'}
                  onClick={() => setActiveTab('bol')}
                >
                  BOL
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  active={activeTab === 'pod'}
                  onClick={() => setActiveTab('pod')}
                >
                  POD
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  active={activeTab === 'invoice'}
                  onClick={() => setActiveTab('invoice')}
                >
                  Invoice
                </NavLink>
              </NavItem>
            </Nav>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const ContactModal = ({ shipment, ...props }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((open) => !open);

  return (
    <React.Fragment>
      <Modal
        centered
        isOpen={open}
        toggle={toggle}
        className="mx-auto"
        style={{ maxWidth: 425, width: '95%' }}
      >
        {() => (
          <ModalBody>
            <h2>We're here for you.</h2>
            <p>
              Straightline has a team of logistics professionals ready to help.
              Give us a shout and we will get back to you ASAP.
            </p>
            <p>
              <Icon type="phone" className="mr-3" />
              <a href={`tel:${process.env.REACT_APP_SUPPORT_PHONE}`}>
                {process.env.REACT_APP_SUPPORT_PHONE}
              </a>
            </p>
            <p>
              <Icon type="mail" className="mr-3" />
              <a
                href={`mailto:${
                  process.env.REACT_APP_SUPPORT_EMAIL
                }?subject=Shipment #${shipment?.id || 'NA'}`}
              >
                {process.env.REACT_APP_SUPPORT_EMAIL}
              </a>
            </p>
            <div className="mt-5">
              <Button
                block
                color="white"
                className="mr-3"
                type="button"
                onClick={toggle}
              >
                Close
              </Button>
            </div>
          </ModalBody>
        )}
      </Modal>
      <Button color="white" {...props} onClick={toggle}>
        <Icon type="send" className="mr-2" />
        Contact
      </Button>
    </React.Fragment>
  );
};

function BolTab({ shipment }) {
  return (
    <Row>
      <Col xl="4" className="order-xl-2">
        <Card>
          <CardBody>
            <Row className="d-flex justify-content-between mb-2">
              <Col className="mb-1">
                <div className="h3 mb-0 d-flex align-items-center">
                  <Icon type="file" className="mr-3" />
                  Bill of Lading
                </div>
              </Col>
              <Col className="col-auto mb-1">
                {/* <div className="mb-0 text-secondary">
                  Updated {formatDistanceToNowStrict(shipment.updated_at)}
                </div> */}
              </Col>
            </Row>
            <div className="mb-3">
              Download the BOL. The Bill of Lading includes shipper/consignee
              info, freight description, and special handling instructions.
            </div>
            <Button
              block
              color="white"
              tag="a"
              disabled={!shipment.carrier_id || !shipment.bol_url}
              href={`${shipment.bol_url}?download=true`}
            >
              Download BOL
            </Button>
          </CardBody>
        </Card>
      </Col>
      <Col xl="8">
        <Card className="position-relative">
          {shipment.carrier_id ? (
            <div>
              <BillOfLading shipment={shipment} />
            </div>
          ) : (
            <div
              className="shadow-sm rounded"
              style={{
                opacity: 0.4,
                width: '100%',
                overflow: 'hidden',
                maskImage:
                  'linear-gradient(to bottom, black 50%, transparent 100%)'
              }}
            >
              <img
                src={invoiceImage}
                style={{ width: '100%' }}
                alt="No Results"
              />
            </div>
          )}
        </Card>
      </Col>
    </Row>
  );
}

function PodTab({ shipment }) {
  const podDocument = shipment.pod_document;

  return (
    <Row>
      <Col xl="4" className="order-xl-2">
        <Card>
          <CardBody>
            <Row className="d-flex justify-content-between mb-2">
              <Col className="mb-1">
                <div className="h3 mb-0 d-flex align-items-center">
                  <Icon type="file" className="mr-3" />
                  Proof of Delivery
                </div>
              </Col>
              <Col className="col-auto mb-1">
                {/* <div className="mb-0 text-secondary">
                  Updated {formatDistanceToNowStrict(shipment.updated_at)}
                </div> */}
              </Col>
            </Row>
            <div className="mb-3">
              Validate and download the Proof of Delivery. This is generally a
              signed Bill of Lading or delivery receipt provided by the carrier
              upon delivery.
            </div>
            <Button
              block
              color="white"
              tag="a"
              disabled={!podDocument?.document.secure_url}
              href={`${podDocument?.document.secure_url}?download=true`}
            >
              Download POD
            </Button>
          </CardBody>
        </Card>
      </Col>
      <Col xl="8">
        <Card className="">
          {podDocument ? (
            <div
              // className="position-absolute text-secondary rounded pt-6 p-4"
              style={{}}
            >
              {podDocument.document.format === 'pdf' ||
              podDocument.document.format === 'xlsx' ? (
                <embed
                  src={podDocument.document.secure_url}
                  type={
                    podDocument.document.format === 'pdf'
                      ? 'application/pdf'
                      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  }
                  className="rounded-top"
                  style={{
                    width: '100%',
                    height: '85vh'
                  }}
                ></embed>
              ) : (
                <div className="rounded-top d-flex align-items-center justify-content-center p-3 h-100">
                  <div>
                    <img
                      src={podDocument.document.secure_url}
                      alt="Shipment Document"
                      className="rounded shadow-lg img-fluid"
                      style={{ maxHeight: '85vh' }}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              className="shadow-sm rounded"
              style={{
                opacity: 0.4,
                width: '100%',
                overflow: 'hidden',
                maskImage:
                  'linear-gradient(to bottom, black 50%, transparent 100%)'
              }}
            >
              <img
                src={invoiceImage}
                style={{ width: '100%' }}
                alt="No Results"
              />
            </div>
          )}
        </Card>
      </Col>
    </Row>
  );
}
